/**
 * Liore share button: `<liore-share data-url data-title data-copied-label>`.
 *
 * Phones open the system share menu (WhatsApp, Messenger, mail…); desktops
 * copy the link and the button says so for three seconds. Same behaviour as
 * the routine builder's share button.
 *
 * Imports nothing from `@theme/*` so the helpers can be unit tested under
 * `node --test`.
 */

/**
 * @param {{ touch: boolean, canShare: boolean, canCopy: boolean }} env
 * @returns {'share' | 'copy' | 'manual'}
 */
export function shareMode({ touch, canShare, canCopy }) {
  if (touch && canShare) return 'share';
  if (canCopy) return 'copy';
  return 'manual';
}

/** A shared link has to work outside this site, so it is always absolute. */
export function absoluteUrl(url, origin) {
  return new URL(String(url ?? ''), origin).href;
}

/**
 * Share or copy one link.
 *
 * @returns {Promise<'shared' | 'cancelled' | 'copied' | 'manual'>}
 */
export async function share({ url, title, origin, touch, navigator: nav }) {
  const href = absoluteUrl(url, origin);
  const mode = shareMode({
    touch: Boolean(touch),
    canShare: typeof nav?.share === 'function',
    canCopy: typeof nav?.clipboard?.writeText === 'function',
  });

  if (mode === 'share') {
    try {
      await nav.share({ title, url: href });
      return 'shared';
    } catch (error) {
      // Closing the share menu is a choice, not a failure.
      if (error?.name === 'AbortError') return 'cancelled';
    }
  }

  if (typeof nav?.clipboard?.writeText === 'function') {
    try {
      await nav.clipboard.writeText(href);
      return 'copied';
    } catch {
      return 'manual';
    }
  }
  return 'manual';
}

if (typeof customElements !== 'undefined' && !customElements.get('liore-share')) {
  class LioreShare extends HTMLElement {
    #timer = 0;

    connectedCallback() {
      this.hidden = false;
      this.querySelector('button')?.addEventListener('click', this.#onClick);
    }

    disconnectedCallback() {
      this.querySelector('button')?.removeEventListener('click', this.#onClick);
      clearTimeout(this.#timer);
    }

    #onClick = async () => {
      const result = await share({
        url: this.dataset.url,
        title: this.dataset.title,
        origin: location.origin,
        touch: window.matchMedia?.('(pointer: coarse)').matches,
        navigator,
      });

      // Feature statistics (assets/liore-track.js): only real shares and copies.
      if (result === 'shared' || result === 'copied') {
        window.lioreTrack?.('article_shared', {
          article: this.dataset.statArticle || '',
          link_location: this.dataset.statLocation || '',
          method: result,
        });
      }

      if (result === 'copied') this.#say(this.dataset.copiedLabel);
      // No share menu and no clipboard: let the reader copy it by hand.
      if (result === 'manual') window.prompt?.('', absoluteUrl(this.dataset.url, location.origin));
    };

    #say(text) {
      const label = this.querySelector('[data-share-label]');
      const status = this.querySelector('[data-share-status]');
      if (!label) return;
      const original = label.dataset.original ?? label.textContent;
      label.dataset.original = original;

      this.toggleAttribute('data-copied', true);
      label.textContent = text;
      // The visible label changes for sighted users; the live region tells
      // screen readers, which do not re-read a button's label on change.
      if (status) status.textContent = text;

      clearTimeout(this.#timer);
      this.#timer = setTimeout(() => {
        this.removeAttribute('data-copied');
        label.textContent = original;
        if (status) status.textContent = '';
      }, 3000);
    }
  }

  customElements.define('liore-share', LioreShare);
}
